import DashboardRequestsTable from '../components/request-table/RequestTable';
import { useUserProfile } from '@hooks';
import { useQuery, useMutation } from '@tanstack/react-query';
import { DASHBOARD_QUERY_KEYS, DashboardQueries } from '@api/services/dashboard';
import { ADDITIONAL_INFO_QUERY_KEYS, AdditionalInfoQueries } from '@api/services/additional-info';
import Loader from '@src/app-components/loader/Loader';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Alert, Button, Label } from '@trussworks/react-uswds';
import Select from 'react-select';
import AdditionalInfoTable from '../components/additional-info-table/AdditionalInfoTable';
import Tabs from '@components/tabs/Tabs';
import { useAdditionalInfoStore, useModal, useUserRoleCapabilities } from '@src/stores';
import { AdditionalInfoFilterStatuses } from '@src/utils/enums';
import '../dashboard.scss';
import AdditionalInfoRequestModal from '../components/additional-info-request/AdditionalInfoRequestModal';
import { queryClient } from '@src/api/queryClient';
import { FILE_STORAGE_QUERY_KEYS } from '@src/api/services/file-storage';
import ArchiveModal from '../components/request-table/RequestArchiveModal';
import { ArchiveMutations } from '@api/services/archive';
import { tSuccess } from '@src/utils/toast-helpers';
import { toast } from 'react-toastify';

const additionalInfoOptions = [
  { value: AdditionalInfoFilterStatuses.Open, label: AdditionalInfoFilterStatuses.Open },
  { value: AdditionalInfoFilterStatuses.Closed, label: AdditionalInfoFilterStatuses.Closed },
];

const handleTabChange = ({
  index,
  setActiveTab,
  setSelectedRequestId,
  setSelectedProjectName,
  setSelectedVersion,
  setPageNumber,
}) => {
  if (index === 1) {
    setSelectedRequestId(null);
    setSelectedProjectName(null);
    setSelectedVersion(0);
    setPageNumber(1);
  }
  setActiveTab(index);
};

const getAdditionalInfoSelectValue = (statusFilters, statusOptions, projectOption) => [
  ...statusFilters
    .map((s) => ({ value: s, label: s }))
    .filter((opt) => statusOptions.some((o) => o.value === opt.value)),
  ...(projectOption ? [projectOption] : []),
];
const getRequestSelectValue = (filteredStatuses, uniqueStatuses) =>
  filteredStatuses
    .map((s) => ({ value: s, label: s }))
    .filter((opt) => uniqueStatuses.some((o) => o.value === opt.value));

const ApplicantDashboard = () => {
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState({});
  const statusOptions = additionalInfoOptions;
  const projectOption = selectedProjectName ? { value: selectedProjectName, label: selectedProjectName } : null;

  const { userRole, isUserRoleValid } = useUserProfile();
  const { roleCapabilities, hasLoadedRoleCapabilities } = useUserRoleCapabilities();
  const canViewDashboard = isUserRoleValid && hasLoadedRoleCapabilities && roleCapabilities.viewDashboard;
  const archived = activeTab === 2;
  const hasSelectedRequests = Object.keys(selectedRequests).length > 0;
  const {
    itemsPerPage,
    pageNumber,
    statusFilters,
    setPageNumber,
    setTotalCount,
    setItemsPerPage,
    sortColumn,
    sortOrder,
    setSortColumn,
    setStatusFilters,
    toggleSortOrder,
  } = useAdditionalInfoStore();
  const { openModal } = useModal();

  const { data, isFetching } = useQuery({
    queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD, userRole, archived],
    queryFn: () => DashboardQueries.getUserRequests(archived),
    enabled: canViewDashboard && !!userRole,
  });

  const { mutate: archiveRequest } = useMutation({
    mutationFn: ArchiveMutations.archiveRequest,
    onMutate: () => toast.loading('Archiving Request...'),
    onSuccess: (_data, _variables, toastId) => {
      tSuccess(toastId, 'Request successfully archived');
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
    },
    onError: (error, _variables, toastId) => {
      toast.dismiss(toastId);
      toast.error(error?.message || 'Request archive failed');
    },
  });

  const { mutate: unArchiveRequest } = useMutation({
    mutationFn: ArchiveMutations.unArchiveRequest,
    onMutate: () => toast.loading('Unarchiving Request...'),
    onSuccess: (_data, _variables, toastId) => {
      tSuccess(toastId, 'Request successfully unarchived');
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
    },
    onError: (error, _variables, toastId) => {
      toast.dismiss(toastId);
      toast.error(error?.message || 'Request unarchive failed');
    },
  });

  const statusParam = statusFilters.length > 1 ? null : statusFilters[0] || null;
  const { data: additionalInfoData, isFetching: isFetchingAdditionalInfo } = useQuery({
    queryKey: [
      ADDITIONAL_INFO_QUERY_KEYS.GET_ALL_REQUESTS,
      pageNumber,
      itemsPerPage,
      statusParam,
      selectedRequestId,
      sortColumn,
      sortOrder,
    ],
    queryFn: () =>
      AdditionalInfoQueries.getAdditionalInfoRequests({
        pageNumber,
        itemsPerPage,
        status: statusParam,
        requestID: selectedRequestId,
        version: selectedVersion,
        sortColumn,
        sortOrder,
      }),
    enabled: canViewDashboard && !!userRole && activeTab === 1,
  });

  useEffect(() => {
    if (additionalInfoData) {
      setTotalCount(additionalInfoData?.totalFilteredCount);
    }
  }, [additionalInfoData, setTotalCount]);

  const requests = useMemo(() => data?.flattenedRequests || [], [data]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    requests.forEach((project) => statuses.add(project.requestStatus));
    return Array.from(statuses).map((status) => ({ value: status, label: status }));
  }, [requests]);

  const filteredRequests = useMemo(() => {
    if (!filteredStatuses.length) return requests;
    return requests.filter((project) => filteredStatuses.includes(project.requestStatus));
  }, [requests, filteredStatuses]);

  const hasOpenAdditionalInfo = useMemo(() => {
    if (!data?.fullProjectData) return false;
    return data.fullProjectData.some((project) =>
      project.requests?.some((req) => req.hasOpenAdditionalInfoRequest === true)
    );
  }, [data]);

  const commonButtonProps = useMemo(
    () => ({
      type: 'button',
      disabled: filteredRequests.length === 0,
    }),
    [filteredRequests.length]
  );

  const handleStatusFilterChange = useCallback((selected) => {
    const values = selected.map((option) => option.value);
    setFilteredStatuses(values);
  }, []);

  const handleAdditionalInfoFilterChange = useCallback(
    (selected) => {
      const statusValues = selected
        .map((option) => option.value)
        .filter((val) => Object.values(AdditionalInfoFilterStatuses).includes(val));
      const projectVal = selected.find((option) => option.value === selectedProjectName);
      if (!projectVal) {
        setSelectedRequestId(null);
        setSelectedProjectName(null);
        setSelectedVersion(0);
      }
      setStatusFilters(statusValues);
      setPageNumber(1);
    },
    [selectedProjectName, setPageNumber, setStatusFilters]
  );

  const handleOpenAdditionalInfo = ({
    request,
    setPageNumber,
    setSelectedRequestId,
    setSelectedProjectName,
    setSelectedVersion,
    setActiveTab,
  }) => {
    if (request?.ormAdditionalInfoActionID) {
      queryClient.invalidateQueries({
        queryKey: [
          ADDITIONAL_INFO_QUERY_KEYS.GET_ADDITIONAL_INFO_FULL,
          request?.ormAdditionalInfoActionID,
          request?.requestID,
          request?.version,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [FILE_STORAGE_QUERY_KEYS.GET_USER_FILE_DETAILS, request?.ormAdditionalInfoActionID],
      });
      openModal(AdditionalInfoRequestModal, {
        ormAdditionalInfoActionID: request?.ormAdditionalInfoActionID,
        requestID: request?.requestID,
        version: request?.version,
        canRespond: true,
        permission: request?.permission,
      });
    } else {
      setSelectedRequestId(request?.requestID);
      setSelectedProjectName(request?.projectName);
      setSelectedVersion(request?.version);
      setPageNumber(1);
      setActiveTab(1);
    }
  };

  const handleViewAllAdditionalInfoClick = ({
    setStatusFilters,
    setSelectedRequestId,
    setSelectedProjectName,
    setSelectedVersion,
    setPageNumber,
    setActiveTab,
  }) => {
    setStatusFilters([AdditionalInfoFilterStatuses.Open]);
    setSelectedRequestId(null);
    setSelectedProjectName(null);
    setSelectedVersion(0);
    setPageNumber(1);
    setActiveTab(1);
  };

  const handleBulkEditClick = useCallback(() => {
    setIsBulkEditing((prev) => {
      if (prev) {
        setSelectedRequests({});
      }
      return !prev;
    });
  }, []);

  const handleBulkSelection = useCallback((version, requestId) => {
    const key = `${requestId}-${version}`;

    setSelectedRequests((prevSelectedRequests) => {
      if (prevSelectedRequests[key]) {
        const newSelectedRequests = { ...prevSelectedRequests };
        delete newSelectedRequests[key];
        return newSelectedRequests;
      } else {
        return {
          ...prevSelectedRequests,
          [key]: {
            requestId,
            version,
          },
        };
      }
    });
  }, []);

  const handleBulkEditSave = useCallback(() => {
    const requests = Object.values(selectedRequests).map(({ requestId, version }) => ({ requestId, version }));
    archived ? unArchiveRequest({ requests }) : archiveRequest({ requests });
    setIsBulkEditing(false);
    setSelectedRequests({});
  }, [archived, selectedRequests, archiveRequest, unArchiveRequest]);

  const handleBulkEditConfirm = useCallback(() => {
    openModal(ArchiveModal, { archived: archived, onArchive: handleBulkEditSave });
  }, [archived, openModal, handleBulkEditSave]);

  const handleSingleArchive = useCallback(
    ({ requestId, version }) =>
      archived
        ? unArchiveRequest({ requests: [{ requestId, version }] })
        : archiveRequest({ requests: [{ requestId, version }] }),
    [archiveRequest, unArchiveRequest, archived]
  );

  return (
    <div>
      {activeTab === 0 && hasOpenAdditionalInfo && (
        <Alert className='margin-bottom-2' type='warning' headingLevel='h4' heading='Additional Information Required'>
          <Button
            unstyled
            type='button'
            className='usa-link'
            onClick={() =>
              handleViewAllAdditionalInfoClick({
                setStatusFilters,
                setSelectedRequestId,
                setSelectedProjectName,
                setSelectedVersion,
                setPageNumber,
                setActiveTab,
              })
            }
          >
            View All Requests Requiring Information
          </Button>
        </Alert>
      )}
      <Tabs
        tabData={[
          {
            title: 'Requests',
            content: (
              <>
                {activeTab === 0 && (
                  <div className='dashboard-controls'>
                    <div className='dashboard-filters'>
                      <div className='project-filter-select'>
                        <Label htmlFor='request-filter-status'>Filter Status</Label>
                        <Select
                          className='basic-multi-select'
                          value={getRequestSelectValue(filteredStatuses, uniqueStatuses)}
                          isMulti
                          name='colors'
                          onChange={handleStatusFilterChange}
                          options={uniqueStatuses}
                          placeholder='All statuses'
                          styles={{
                            menu: (provided) => ({ ...provided, width: 'auto', minWidth: 140 }),
                            control: (provided) => ({ ...provided, minWidth: 140 }),
                          }}
                        />
                      </div>
                    </div>
                    <div className='dashboard-bulk-actions'>
                      {!isBulkEditing && (
                        <button
                          className='table-input-button primary'
                          onClick={handleBulkEditClick}
                          {...commonButtonProps}
                        >
                          Archive Requests
                        </button>
                      )}
                      {isBulkEditing && (
                        <>
                          <button className='table-input-button' onClick={handleBulkEditClick} {...commonButtonProps}>
                            Cancel
                          </button>
                          <button
                            className='table-input-button primary'
                            onClick={handleBulkEditConfirm}
                            disabled={!hasSelectedRequests}
                            {...commonButtonProps}
                          >
                            Archive Selected
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <Loader showLoader={isFetching} variant='page' />
                {isFetching ? null : (
                  <DashboardRequestsTable
                    isBulkEditing={isBulkEditing}
                    rowData={filteredRequests}
                    selectedRequests={selectedRequests}
                    onBulkSelection={handleBulkSelection}
                    onSingleArchive={handleSingleArchive}
                    onOpenAdditionalInfo={(request) =>
                      handleOpenAdditionalInfo({
                        request,
                        setPageNumber,
                        setSelectedRequestId,
                        setSelectedProjectName,
                        setSelectedVersion,
                        setActiveTab,
                      })
                    }
                  />
                )}
              </>
            ),
          },
          {
            title: 'Requests Requiring Information',
            content: (
              <>
                {activeTab === 1 && (
                  <div className='project-filter-select'>
                    <Label htmlFor='request-filter-status'>Filter Options</Label>
                    <Select
                      className='basic-multi-select'
                      value={getAdditionalInfoSelectValue(statusFilters, statusOptions, projectOption)}
                      isMulti
                      onChange={handleAdditionalInfoFilterChange}
                      options={[...statusOptions, ...(projectOption ? [projectOption] : [])]}
                      placeholder='All options'
                      styles={{
                        menu: (provided) => ({ ...provided, width: 'auto', minWidth: 140 }),
                        control: (provided) => ({ ...provided, minWidth: 140 }),
                      }}
                    />
                  </div>
                )}
                <Loader showLoader={isFetchingAdditionalInfo} variant='page' />
                {isFetchingAdditionalInfo ? null : (
                  <AdditionalInfoTable
                    additionalInfoRequests={additionalInfoData?.requestsRequiringInformation || []}
                    totalFilteredCount={additionalInfoData?.totalFilteredCount || 0}
                    pageNumber={pageNumber}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setPageNumber}
                    onPageSizeChange={setItemsPerPage}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    setSortColumn={setSortColumn}
                    toggleSortOrder={toggleSortOrder}
                  />
                )}
              </>
            ),
          },
          {
            title: 'Archived Requests',
            content: (
              <>
                {activeTab === 2 && (
                  <div className='dashboard-controls'>
                    <div className='dashboard-filters'>
                      <div className='project-filter-select'>
                        <Label htmlFor='request-filter-status'>Filter Status</Label>
                        <Select
                          className='basic-multi-select'
                          value={getRequestSelectValue(filteredStatuses, uniqueStatuses)}
                          isMulti
                          name='colors'
                          onChange={handleStatusFilterChange}
                          options={uniqueStatuses}
                          placeholder='All statuses'
                          styles={{
                            menu: (provided) => ({ ...provided, width: 'auto', minWidth: 140 }),
                            control: (provided) => ({ ...provided, minWidth: 140 }),
                          }}
                        />
                      </div>
                    </div>
                    <div className='dashboard-bulk-actions'>
                      {!isBulkEditing && (
                        <button
                          className='table-input-button primary'
                          onClick={handleBulkEditClick}
                          {...commonButtonProps}
                        >
                          Unarchive Requests
                        </button>
                      )}
                      {isBulkEditing && (
                        <>
                          <button className='table-input-button' onClick={handleBulkEditClick} {...commonButtonProps}>
                            Cancel
                          </button>
                          <button
                            className='table-input-button primary'
                            onClick={handleBulkEditConfirm}
                            disabled={!hasSelectedRequests}
                            {...commonButtonProps}
                          >
                            Unarchive Selected
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <Loader showLoader={isFetching} variant='page' />
                {isFetching ? null : (
                  <DashboardRequestsTable
                    isBulkEditing={isBulkEditing}
                    rowData={filteredRequests}
                    selectedRequests={selectedRequests}
                    onBulkSelection={handleBulkSelection}
                    onSingleArchive={handleSingleArchive}
                    onOpenAdditionalInfo={(request) =>
                      handleOpenAdditionalInfo({
                        request,
                        setPageNumber,
                        setSelectedRequestId,
                        setSelectedProjectName,
                        setSelectedVersion,
                        setActiveTab,
                      })
                    }
                    archived={archived}
                  />
                )}
              </>
            ),
          },
        ]}
        activeIndex={activeTab}
        onTabChange={(index) =>
          handleTabChange({
            index,
            setActiveTab,
            setSelectedRequestId,
            setSelectedProjectName,
            setSelectedVersion,
            setPageNumber,
          })
        }
      />
    </div>
  );
};
export default ApplicantDashboard;
